import React, { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client';
import { ICategory } from '../../../types/org';
import { Table } from 'reactstrap';
import { GET_INFOGO_ROOT_ORGS, SAVE_CATEGORY_CONFIG, } from '../../../graphql/infogo-gql';
import OrgItem from './org-item';
import { DeleteIcon, SaveIcon } from '../../common/icons';
import { IInfoGoOrg, IPresentationInput } from '../../../types/infogo';
import { PresentationObject } from '../../../types/constants';
import { notifySuccess } from '../../../services/notification';
import SpinnerOverlay from '../../common/spinner/spinner';
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import useAuth from '../../../hook/useAuth';
import Button from '../../common/button';

type InfoGoConfigProps = {
    category?: ICategory;
}

const TABLE_HEADERS: TableHeaderInfo<IInfoGoOrg>[] = [
    { header: 'Organization', accessor: 'orgName', },
    { header: 'Start Date', accessor: 'createDate', cssClass: 'date-column date-column-mmm-dd-yyyy' },
    { header: 'Suppressed?', accessor: 'suppress', cssClass: 'date-column date-column-mmm-dd-yyyy' } // not actually a date, just happens to be the same width
];

const InfoGoConfig = (props: InfoGoConfigProps) => {
    const { category } = props
    const [orgs, setOrgs] = useState<IInfoGoOrg[]>([])
    const { isAdmin,isFrenchTranslator } = useAuth();

    const [saveCategoryConfig, { loading: loadingSave }] = useMutation(SAVE_CATEGORY_CONFIG)

    const [suppressedIds, setSuppressedIds] = useState<string[]>([])

    const [prevSuppress, setPrevSuppress] = useState<string[]>([])

    const [activeId, setActiveId] = useState<string | null>(null);

    const [getRootOrgs, { loading, data }] = useLazyQuery(GET_INFOGO_ROOT_ORGS, {
        onCompleted: (data) => {
            if (data?.infoGoRootOrgs) {
                const suppressVls = data.infoGoRootOrgs.filter((e: IInfoGoOrg) => e.suppress).map((e: IInfoGoOrg) => e.relationshipId)
                setPrevSuppress(suppressVls)
                setSuppressedIds(suppressVls)
            }
            setOrgs(data?.infoGoRootOrgs ? [...data.infoGoRootOrgs].sort((a,b)=>a.orgName.localeCompare(b.orgName)) : [])
        }
    })

    useEffect(() => {
        if (category) {
            getRootOrgs({ variables: { categoryId: category.id } })
        }
    }, [category])

    const onReset = () => {
        setOrgs(data?.infoGoRootOrgs ? [...data.infoGoRootOrgs].sort((a,b)=>a.orgName.localeCompare(b.orgName)) : [])
        setSuppressedIds(prevSuppress)
    }

    const onSave = () => {
        let input: IPresentationInput[] = orgs.map((o: IInfoGoOrg, index: number) => {
            return {
                typeName: PresentationObject.GENERAL_RELATIONSHIP,
                objectId: o.relationshipId,
                suppress: suppressedIds.includes(o.relationshipId) ? true : false,
                sequence: index + 1
            }
        })

        saveCategoryConfig({
            variables: {
                categoryId: category?.id,
                input: {
                    orgConfig: input
                }
            },
            refetchQueries: [{
                query: GET_INFOGO_ROOT_ORGS,
                variables: { categoryId: category?.id }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => {
                data && notifySuccess()
            }
        })
    }

    const hasSuppressChange = (): boolean => {
        if (prevSuppress.length !== suppressedIds.length) {
            return true
        }
        if (prevSuppress.length > 0) {
            return prevSuppress.some((value) => !suppressedIds.includes(value))
        }
        return false
    }

    const hasChange = (): boolean => {
        return hasSuppressChange()
    }

    const onSuppressCheck = (event: any, id: string) => {
        if (event.target.checked) {
            setSuppressedIds([...suppressedIds, id])
        } else {
            setSuppressedIds(suppressedIds.filter(e => e != id))
        }
    }

    return (
        <>
            {(loading || loadingSave) && <SpinnerOverlay />}
            {!isFrenchTranslator && <div>
                <Button color='primary' onClick={onSave} style={{ marginRight: '1rem' }} disabled={!hasChange()}>
                    <SaveIcon /> Save Changes
                </Button>
                <Button color='danger' onClick={onReset} disabled={!hasChange()}>
                    <DeleteIcon /> Discard Changes
                </Button>
            </div>}
            {
                orgs.length === 0
                ?
                <div style={{ textAlign: 'center' }}>No organizations found.</div>
                :
                <Table striped id="infogo_orgs">
                    <TableHeaders headers={TABLE_HEADERS}/>
                    <tbody>
                        {orgs.map((org: IInfoGoOrg) => (
                            <OrgItem
                                key={org.orgId}
                                id={org.orgId}
                                org={org}
                                suppressed={suppressedIds.includes(org.relationshipId)}
                                onSuppressCheck={event => onSuppressCheck(event, org.relationshipId)} />
                        ))}
                    </tbody>
                </Table>
            }
        </>
    )
}

export default InfoGoConfig
